/**
 * Point an applet-backed grid at one record, so it shows that record's rows and not the realm's.
 *
 * THE ORDER IS THE WHOLE POINT, and getting it wrong is silent. In the enterprise component:
 *
 *   - `newQuery()` opens a query buffer and clears the search specification
 *   - `setAttributeValue()` writes the filter INTO that query buffer
 *   - `invalidate()` THROWS THE QUERY BUFFER AWAY, sets MODE_IDLE, and then synchronously notifies
 *     its listeners — and `PlatformTable.enterpriseComponentInvalidated` responds by calling
 *     `executeQuery(1, pageSize)` immediately
 *
 * So `newQuery(); setAttributeValue(...); invalidate();` — which reads like "configure, then
 * refresh" — discards the filter and then fires an UNFILTERED query. The grid fills with every row
 * in the realm and nothing reports an error, because an empty query is a legal query. That is
 * exactly what the History tab did: DOC-00004 showed other objects' audit rows.
 *
 * The fix is to configure and then fetch explicitly, never invalidating afterwards. `executeQuery`
 * in MODE_QUERY clears the results buffer itself, so the stale rows go without `invalidate`.
 *
 * AND THE FILTER GOES IN THE SCOPE, NOT THE QUERY BUFFER. `PlatformTable` calls `newQuery()` on
 * every sort, refresh and search reset, which resets the query buffer and then re-applies only what
 * the table itself knows about -- sort, search, visibility. A filter left in the buffer therefore
 * survives the first render and is thrown away by the first re-sort, and the grid silently widens to
 * the whole realm. `setScope` is the channel that outlives newQuery(); see EnterpriseComponent.
 */

/** Matches the PageSize default on the table applets this scopes. */
export const GRID_PAGE_SIZE = 20;

export type QueryableEC = {
    newQuery?: () => void;
    setScope?: (name: string, value: any) => void;
    executeQuery?: (page: number, pageSize: number) => void;
    invalidate?: () => void;
    fetchEnterpriseComponentSuccess?: (data: any, page: number, pageSize: number) => void;
};

/**
 * @param ec         the grid's enterprise component; may be undefined while the EO is building
 * @param attribute  the foreign key to filter on, by EC attribute name
 * @param value      what to filter it to. Falsy means "not resolved yet" and does nothing, rather
 *                   than querying for everything — an unscoped grid is the bug this exists to stop.
 */
export function scopeGridTo(ec: QueryableEC | undefined | null, attribute: string,
                            value: string | undefined | null,
                            pageSize: number = GRID_PAGE_SIZE): boolean {
    if (!ec || !value) return false;
    if (!ec.newQuery || !ec.setScope || !ec.executeQuery) return false;

    ec.setScope(attribute, value);
    ec.newQuery();
    ec.executeQuery(1, pageSize);
    return true;
}


/**
 * Fill a grid from rows already in hand, instead of fetching them again.
 *
 * The detail document returns a record's relationships nested inside the record, so by the time the
 * page has the record it also has the rows this grid was about to ask the server for. Seeding costs
 * nothing and saves a round trip on the tab nobody has opened yet.
 *
 * THE SCOPE IS STILL SET, and that is what makes this safe rather than clever. `PlatformTable` calls
 * `newQuery()` and re-queries on every sort, search and refresh -- seeded rows do not survive the
 * first interaction, and are not meant to. What survives is the scope, so the re-query asks for this
 * record's rows and not the realm's. The worst case is therefore one redundant fetch and identical
 * behaviour to not seeding at all; there is no arrangement of events where this shows the wrong rows.
 *
 * Seeding populates page 1 of the results buffer, which is also what makes the table's own mount
 * query fall through: `executeQuery` short-circuits when the page it wants is already buffered. That
 * is the same short-circuit that makes the mount race in reference_enliltable_mount_query_race
 * misbehave; here it is doing exactly what it was built for.
 */
export function seedGridWith(ec: QueryableEC | undefined | null, attribute: string,
                             value: string | undefined | null, rows: any[] | undefined | null,
                             pageSize: number = GRID_PAGE_SIZE): boolean {
    if (!ec || !value) return false;
    if (!ec.setScope || !ec.newQuery || !ec.fetchEnterpriseComponentSuccess) return false;
    if (!Array.isArray(rows)) return false;

    ec.setScope(attribute, value);
    ec.newQuery();
    // An empty list is a real answer -- "this record has no relationships" -- and seeding it stops
    // the grid asking the server a question the document already answered.
    ec.fetchEnterpriseComponentSuccess({Results: rows}, 1, pageSize);
    return true;
}
